import styles from './index.scss';
import { Header } from '../components/Header';
import { Text } from 'components/typography';
import { ErrorMessage, Loading } from 'components';
import { useOnboardingBuildings } from 'lane-shared/contexts/SignUpContext/useOnboardingBuildings';
import { Channel } from 'lane-shared/types/ChannelType';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { OnboardingPreselectedBuilding } from './OnboardingPreselectedBuilding';
import { OnboardingSelectBuilding } from './OnboardingSelectBuilding';
import { convertBuildingEdgeToChannelType } from 'components/onboarding/components/ChannelListItemWrapper/ChannelListItemWrapper';
import { OnboardingCompleteData } from '..';

const TRANSLATION_KEYS = {
  serverError: 'web.onboarding.building.server.error',
};

type Props = {
  building: Channel | null;
  onboardingState: 'loading' | 'idle' | 'error';
  errorMessage?: string;
  userName: string;
  onComplete: (building: Channel, userName: string) => void;
  onOnboardingComplete: (data: OnboardingCompleteData) => void;
  onContinue: (building: Channel) => void;
  onClear: () => void;
  invitedChannel?: Channel;
  presumedEmailDomain?: string;
};

export function OnboardingBuilding({
  building,
  onboardingState,
  userName,
  errorMessage,
  invitedChannel,
  presumedEmailDomain,
  onComplete,
  onOnboardingComplete,
  onContinue,
  onClear,
}: Props) {
  const { t } = useTranslation();
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();

  const [shouldUsePreselectedBuilding, setShouldUsePreselectedBuilding] =
    useState(true);

  const onboardingBuildingResult = useOnboardingBuildings({
    query: '',
    channelId: invitedChannel?._id,
    presumedEmailDomain,
  });

  const preselectedBuildings = onboardingBuildingResult.buildings.filter(
    b => b.node.matchesEmailDomain
  );
  const preselectedBuilding =
    preselectedBuildings.length === 1
      ? convertBuildingEdgeToChannelType(preselectedBuildings[0])
      : null;

  const shouldShowPreselectedBuilding =
    isWorkplaceOnboardingEnabled &&
    shouldUsePreselectedBuilding &&
    preselectedBuilding &&
    !invitedChannel;

  const isLoading =
    onboardingState === 'loading' || onboardingBuildingResult.isInitialLoading;

  const shouldRedirectDirectlyToLogin =
    !errorMessage &&
    !isLoading &&
    isWorkplaceOnboardingEnabled &&
    invitedChannel &&
    onboardingBuildingResult.buildings.length <= 1;

  useEffect(() => {
    if (onboardingBuildingResult.error) {
      window.Toast.show(<Text>{t(TRANSLATION_KEYS.serverError)}</Text>);
    }
  }, [onboardingBuildingResult.error]);

  const handleOtherLocationClick = () => {
    setShouldUsePreselectedBuilding(false);
  };

  if (shouldRedirectDirectlyToLogin) {
    const buildingEdge = onboardingBuildingResult.buildings?.[0];

    const building = buildingEdge
      ? convertBuildingEdgeToChannelType(buildingEdge)
      : undefined;
    const company = invitedChannel;

    if (company) {
      onOnboardingComplete({ company, building, userName });
    }
  }

  return (
    <div className={styles.container}>
      <Header userName={userName} />
      {errorMessage && <ErrorMessage error={errorMessage} />}
      {isLoading && <Loading className={styles.loading} />}
      {!isLoading &&
        (shouldShowPreselectedBuilding ? (
          <OnboardingPreselectedBuilding
            onboardingState={onboardingState}
            preselectedBuilding={preselectedBuilding}
            onContinue={onContinue}
            handlers={{
              handleOtherLocationClick,
            }}
          />
        ) : (
          <OnboardingSelectBuilding
            building={building}
            onboardingState={onboardingState}
            userName={userName}
            onComplete={onComplete}
            onContinue={onContinue}
            onClear={onClear}
            presumedEmailDomain={presumedEmailDomain}
            invitedChannel={invitedChannel}
          />
        ))}
    </div>
  );
}
